import React, { useState, useEffect, useRef } from "react";
import "../../../styles/utils/modal/NovelFinalModal.scss";
import {
  customCreateTitle,
  customCreateCoverPrompt,
  customCreateCoverImage,
  customSaveBook,
} from "../../../api/customAPI";
import { getStoryImageStatus } from "../../../api/StoryAPI";
import Loading from "../loading/Loading";
import MagicLoading from "../loading/MagicLoading";
import NikiPencilLoading from "../loading/NikiPencilLoading";
import CompleteModal from "../../creating/modal/CompleteModal";
import ErrorModal from "./ErrorModal";
import { WordFilter } from "../../../utils/wordFilter/WordFilter";
import MusicToggleButton from "../button/MusicToggleButton";
import useAppTranslation from "../../../hooks/useAppTranslation";
import { getLocalizedAssetPath } from "../../../utils/getLocalizedLogo";

const STEPS = [
  {
    id: "step-title",
    titleKey: "steps.title.title",
    descKey: "steps.title.desc",
    btnKey: "steps.title.button",
    className: "step-title",
  },
  {
    id: "step-cover",
    titleKey: "steps.cover.title",
    descKey: "steps.cover.desc",
    btnKey: "steps.cover.button",
    className: "step-cover",
  },
  {
    id: "step-save",
    titleKey: "steps.save.title",
    descKey: "steps.save.desc",
    btnKey: "steps.save.button",
    className: "step-save",
  },
];

export default function NovelFinalModal({
  story,
  contentList,
  onClose,
  user_id,
  character_job_id,
  storyPrompt,
  storyId,
}) {
  const { t, ready } = useAppTranslation("components/utils/novelFinalModal");
  const [currentStep, setCurrentStep] = useState(0);
  const [bookTitle, setBookTitle] = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingTitle, setLoadingTitle] = useState("");
  const [loadingFlag, setLoadingFlag] = useState("");
  const [generateEnd, setGenerateEnd] = useState(false);

  const [studentName, setStudentName] = useState("");
  const [showNameModal, setShowNameModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [savedBookId, setSavedBookId] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const [showTitleOnCover, setShowTitleOnCover] = useState(true);

  const [showTitleEditModal, setShowTitleEditModal] = useState(false);
  const [tempTitle, setTempTitle] = useState("");

  const controlLoading = (on) => {
    if (on) {
      setGenerateEnd(false);
      setShowLoading(true);
    } else {
      setGenerateEnd(true);
      setShowLoading(false);
    }
  };

  // 제목 수정 모달 열기
  const handleOpenTitleEditModal = () => {
    setTempTitle(bookTitle);
    setShowTitleEditModal(true);
  };

  // 제목 수정 저장
  const handleSaveEditedTitle = () => {
    if (!tempTitle.trim()) {
      alert(t("alerts.enterTitle"));
      return;
    }

    if (WordFilter(tempTitle)) {
      alert(t("alerts.inappropriateText"));
      return;
    }
    setBookTitle(tempTitle.trim());
    setShowTitleEditModal(false);
  };

  // 제목 생성 핸들러
  const handleMakeTitle = async () => {
    setLoadingTitle(t("loading.makeTitle"));
    setLoadingFlag("title");
    controlLoading(true);

    try {
      const data = {
        user_id: user_id,
        story_id: storyId,
        items: storyPrompt,
      };

      const res = await customCreateTitle(data);
      if (!res?.success) {
        throw new Error(res?.errMsg || t("errors.titleGenerationFailed"));
      }

      let title = res.data;
      if (typeof title === "string" && title.includes('"')) {
        title = title.replaceAll('"', "");
      }

      setTimeout(() => {
        setBookTitle(title);
        controlLoading(false);
        setCurrentStep(1);
      }, 2000);
    } catch (e) {
      console.error("[handleMakeTitle error]", e);
      controlLoading(false);
      alert(t("errors.makeTitleFailed"));
    }
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      handleMakeTitle();
    } else if (currentStep === 1) {
      handleGenerateCover();
    } else {
      setShowNameModal(true);
    }
  };

  const stepData = STEPS[currentStep];

  // 표지 내부 콘텐츠 렌더링
  const renderCoverInside = () => {
    switch (stepData.id) {
      case "step-title":
        return (
          <div className="cover-init">
            <p>
              {t("coverInit.line1")} <br />
              <span>{t("coverInit.highlight")}</span>
              {t("coverInit.line2")}
            </p>
          </div>
        );
      case "step-cover":
        return (
          <div className="cover-title-ready">
            <span className="badge">{t("status.titleComplete")}</span>
            <h2 className="novel-title">
              {bookTitle || t("fallbackTitle")}
            </h2>
            <div className="sub-buttons">
              <button onClick={handleOpenTitleEditModal} className="btn-direct">
                {t("actions.editTitle")}
              </button>
              <button onClick={handleMakeTitle} className="btn-retry">
                {t("actions.remakeTitle")}
              </button>
            </div>
          </div>
        );
      case "step-save":
        return (
          <div className="cover-final">
            <div className="cover-title-toggle-tabs">
              <button
                className={`toggle-tab ${showTitleOnCover ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTitleOnCover(true);
                }}
              >
                <img
                  src={
                    showTitleOnCover
                      ? getLocalizedAssetPath("/images/makingBranch/title-on.svg")
                      : getLocalizedAssetPath("/images/makingBranch/title-off.svg")
                  }
                  alt={t("alts.showTitle")}
                  loading="lazy"
                  decoding="async"
                />
              </button>
              <button
                className={`toggle-tab ${!showTitleOnCover ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTitleOnCover(false);
                }}
              >
                <img
                  src={
                    showTitleOnCover
                      ? getLocalizedAssetPath("/images/makingBranch/cover-off.svg")
                      : getLocalizedAssetPath("/images/makingBranch/cover-on.svg")
                  }
                  alt={t("alts.showCover")}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </div>
            <div className="img-wrap">
              <img
                src={backgroundImage || "/images/makingBranch/arial-mock.svg"}
                alt={t("alts.finalCover")}
                loading="lazy"
                decoding="async"
              />
            </div>
            <h2 className={`novel-title ${!showTitleOnCover ? "hide" : ""}`}>
              {bookTitle}
            </h2>
            <button
              className={`retry-btn ${!showTitleOnCover ? "hide" : ""}`}
              onClick={handleGenerateCover}
            >
              {t("actions.remakeCover")}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // 표지 이미지 생성 및 폴링 로직
  const handleGenerateCover = async () => {
    setLoadingTitle(t("loading.makeCover"));
    setLoadingFlag("cover");
    controlLoading(true);

    try {
      // 1. 프롬프트 생성
      const promptData = {
        user_id,
        story_id: storyId,
        title: bookTitle,
        items: storyPrompt,
      };
      const res_prompt = await customCreateCoverPrompt(promptData);
      if (!res_prompt?.success) throw new Error(t("errors.coverPromptFailed"));

      // 2. 이미지 생성 시작
      const imageData = {
        user_id,
        character_job_id,
        background_prompt: res_prompt.data.background_prompt,
        character_prompt: res_prompt.data.character_prompt,
      };
      const res_image = await customCreateCoverImage(imageData);
      if (!res_image?.success) throw new Error(t("errors.coverImageStartFailed"));

      const { batch_id } = res_image.data || {};

      // 3. 폴링 (Polling)
      const timeoutMs = 120000;
      const startedAt = Date.now();
      let coverUrl = "";

      while (true) {
        const statusRes = await getStoryImageStatus({ batch_id, user_id });
        if (!statusRes?.success) throw new Error(t("errors.statusFailed"));

        const d = statusRes.data || {};
        if (d.status === "failed") throw new Error(t("errors.generationFailed"));

        if (d.all_completed && d.images?.length > 0) {
          coverUrl = d.images[0]?.url || "";
          break;
        }

        if (Date.now() - startedAt > timeoutMs) throw new Error(t("errors.timeout"));
        await sleep(2000);
      }

      if (coverUrl) {
        setBackgroundImage(coverUrl);
        controlLoading(false);
        setCurrentStep(2); // 저장하기 단계로 이동
      }
    } catch (e) {
      console.error("[handleGenerateCover error]", e);
      controlLoading(false);
      alert(t("errors.coverGenerationFailed"));
    }
  };

  // 최종 저장 로직
  const handleSaveFinalBook = async () => {
    if (!studentName.trim()) {
      alert(t("alerts.enterName"));
      return;
    }

    if (WordFilter(studentName)) {
      alert(t("alerts.inappropriateText"));
      return;
    }

    setShowNameModal(false);
    setLoadingFlag("save");
    controlLoading(true);
    setLoadingTitle(t("loading.saveNovel"));

    try {
      const fullTitle = `${bookTitle} - ${studentName.trim()}`;
      const body = {
        user_id,
        character_job_id,
        title: fullTitle,
        cover_url: backgroundImage,
        items: story,
      };

      const res = await customSaveBook(body);
      const row = Array.isArray(res) ? res[0] : (res?.data ?? res);
      const newBookId = row?.book_id ?? row?.id;

      if (!newBookId) throw new Error(t("errors.saveFailed"));

      setSavedBookId(newBookId);
      controlLoading(false);
      setShowCompleteModal(true);
    } catch (e) {
      console.error("[handleSaveFinalBook error]", e);
      controlLoading(false);
      alert(t("errors.saveFailed"));
    }
  };

  if (!ready) {
    return null;
  }

  return (
    <div className="novel-final-modal-wrap">
      {showLoading && (
        <>
          {loadingFlag === "cover" ? (
            <MagicLoading />
          ) : loadingFlag === "title" ? (
            <NikiPencilLoading />
          ) : (
            <Loading
              title={loadingTitle}
              end={generateEnd}
              flag={loadingFlag}
              onEnd={() => setShowLoading(false)}
            />
          )}
        </>
      )}
      <div className="result-book-container">
        <div className="result-book">
          <div className="cover-content">{renderCoverInside()}</div>
        </div>
        <figure className="result-book-left">
          <img
            src="/images/utils/modal/novelFinalModal/book-left.svg"
            alt={t("alts.bookSpine")}
            loading="lazy"
            decoding="async"
          />
        </figure>
        <figure className="result-book-bottom">
          <img
            src="/images/utils/modal/novelFinalModal/book-bottom.svg"
            alt={t("alts.bookPages")}
            loading="lazy"
            decoding="async"
          />
        </figure>
      </div>

      <figure className="arial-asset">
        <img
          src="/images/utils/modal/novelFinalModal/arial.svg"
          alt={t("alts.arial")}
          loading="lazy"
          decoding="async"
        />
      </figure>

      {!showLoading && (
        <div className="music-toggle-btn-final-wrapper">
          <MusicToggleButton />
        </div>
      )}

      <div className="bubble-asset">
        <img
          src="/images/utils/modal/novelFinalModal/bubble.svg"
          alt={t("alts.bubble")}
          loading="lazy"
          decoding="async"
        />
        <div className="bubble-content">
          <p className="bubble-title">{t(stepData.titleKey)}</p>
          <p className="bubble-desc">{t(stepData.descKey)}</p>
          <button
            className={`bubble-btn ${stepData.className}`}
            onClick={handleNextStep}
          >
            {t(stepData.btnKey)}
          </button>
        </div>
      </div>

      {!showLoading && (
        <div className="go-back-btn" onClick={onClose}>
          {t("actions.goBack")}
        </div>
      )}

      {showTitleEditModal && (
        <div className="name-modal-overlay">
          <div className="name-modal">
            <h3>{t("titleEditModal.title")}</h3>
            <input
              type="text"
              placeholder={t("titleEditModal.placeholder")}
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
            />
            <div className="btn-group">
              <button
                className="cancel"
                onClick={() => setShowTitleEditModal(false)}
              >
                {t("actions.cancel")}
              </button>
              <button className="save" onClick={handleSaveEditedTitle}>
                {t("actions.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNameModal && (
        <div className="name-modal-overlay">
          <div className="name-modal">
            <h3>{t("nameModal.title")}</h3>
            <input
              type="text"
              placeholder={t("nameModal.placeholder")}
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
            />
            <div className="btn-group">
              <button
                className="cancel"
                onClick={() => setShowNameModal(false)}
              >
                {t("actions.cancel")}
              </button>
              <button className="save" onClick={handleSaveFinalBook}>
                {t("actions.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompleteModal && (
        <CompleteModal
          contents_json={contentList}
          title={bookTitle}
          backgroundImage={backgroundImage}
          bookId={savedBookId}
        />
      )}

      {errorInfo && (
        <ErrorModal
          error_message={errorInfo.message}
          onClose={() => setErrorInfo(null)}
        />
      )}
    </div>
  );
}
